import {
  Chips,
  ChevronDownIcon,
  HoverCard,
  Select,
  Text,
  Tooltip,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { ResponsiveSegmentedControl } from '../ResponsiveSegmentedControl'
import type { SegmentedEntry } from '../ResponsiveSegmentedControl'
import type { MetricSeries } from '../MetricChart/types'
import type { NarrativeView, NarrativeViewKey } from './narrativeViews'

import styles from './MetricsNarrativeChart.module.css'

import type { JSX } from 'react'

// Shown on the Posts split-button caret when only one metric has data for the
// selected range, so there's nothing to switch to. The caret is disabled and
// this explains why, rather than opening a pointless one-item menu.
const NO_OTHER_METRICS_TOOLTIP =
  'No other metrics have data for the selected dates'

// Single-metric options share the collapsed dropdown's value space with the
// composite views, so their values are prefixed to keep the two key sets from
// ever colliding.
const METRIC_VALUE_PREFIX = 'metric:'

interface NarrativeTabBarProps {
  availableViews: NarrativeView[]
  selectedViewKey: NarrativeViewKey
  singleMetricKey: string
  singleMetricTitle: string
  singleMetricOptions: MetricSeries[]
  onViewChange: (key: NarrativeViewKey) => void
  onSingleMetricChange: (key: string) => void
}

export function NarrativeTabBar({
  availableViews,
  selectedViewKey,
  singleMetricKey,
  singleMetricTitle,
  singleMetricOptions,
  onViewChange,
  onSingleMetricChange,
}: NarrativeTabBarProps): JSX.Element {
  // Collapsed dropdown (narrow cards): composite views as top-level items, then
  // the single-metric views under a group label. Picking a metric switches to
  // the Posts view showing that metric (onSingleMetricChange sets the view).
  const compositeViews = availableViews.filter((view) => view.key !== 'posts')
  const collapsedOptions: SegmentedEntry[] = [
    ...compositeViews.map((view) => ({
      value: view.key,
      label: view.title,
      icon: view.icon,
      // Same copy as the desktop chip's info HoverCard, surfaced inline so the
      // mobile drawer explains what each composite view shows.
      description: view.description,
    })),
    {
      label: 'Single metric',
      options: singleMetricOptions.map((metric) => ({
        value: `${METRIC_VALUE_PREFIX}${metric.key}`,
        label: metric.title,
        icon: metric.icon,
      })),
    },
  ]
  const collapsedValue =
    selectedViewKey === 'posts'
      ? `${METRIC_VALUE_PREFIX}${singleMetricKey}`
      : selectedViewKey
  const handleCollapsedChange = (value: string): void => {
    if (value.startsWith(METRIC_VALUE_PREFIX)) {
      onSingleMetricChange(value.slice(METRIC_VALUE_PREFIX.length))
      return
    }
    const compositeView = compositeViews.find((view) => view.key === value)
    if (compositeView) {
      onViewChange(compositeView.key)
    }
  }

  return (
    <ResponsiveSegmentedControl
      value={collapsedValue}
      onChange={handleCollapsedChange}
      options={collapsedOptions}
    >
      <Chips
        required
        value={selectedViewKey}
        onChange={(value): void => {
          const view = availableViews.find(
            (candidate) => candidate.key === value,
          )
          if (view) onViewChange(view.key)
        }}
        className={styles.tabBar}
      >
        {availableViews.map((view) => {
          // The "Posts" tab is a split button: the main region activates the
          // Posts view with the currently-selected metric (without opening the
          // menu), and the caret opens the dropdown to switch metrics. The
          // trigger icon reflects the selected metric, not a static Posts icon.
          if (view.key === 'posts') {
            const isPostsSelected = selectedViewKey === 'posts'
            const selectedMetricIcon =
              singleMetricOptions.find((m) => m.key === singleMetricKey)
                ?.icon ?? view.icon
            // When only one metric has data for the selected range there is
            // nothing to switch to, so the caret is disabled with an explanatory
            // tooltip instead of opening a one-item menu.
            const cannotSwitchMetric = singleMetricOptions.length <= 1
            return (
              <Select
                key="posts"
                value={singleMetricKey}
                onValueChange={(key): void => {
                  onSingleMetricChange(key)
                }}
              >
                <span
                  className={styles.metricSplit}
                  data-selected={isPostsSelected ? '' : undefined}
                >
                  <button
                    type="button"
                    className={styles.metricSplitMain}
                    aria-pressed={isPostsSelected}
                    onClick={() => onViewChange('posts')}
                  >
                    <span className={styles.tabIcon} aria-hidden="true">
                      {selectedMetricIcon}
                    </span>
                    {singleMetricTitle}
                  </button>
                  {cannotSwitchMetric ? (
                    // Nothing to switch to: render the caret disabled and outside
                    // Select.Trigger so it can never open the menu. Uses
                    // `aria-disabled` rather than the native `disabled` attribute,
                    // which would suppress the hover/focus events the Tooltip needs
                    // to open for pointer and keyboard users alike.
                    <Tooltip content={NO_OTHER_METRICS_TOOLTIP}>
                      <button
                        type="button"
                        className={styles.metricSplitCaret}
                        aria-disabled="true"
                        aria-label={NO_OTHER_METRICS_TOOLTIP}
                      >
                        <ChevronDownIcon size="xsmall" aria-hidden />
                      </button>
                    </Tooltip>
                  ) : (
                    /* Select.Trigger applies Radix `asChild` internally, so its
                     single child becomes the trigger — the caret opens the
                     metric menu while the main button (a sibling) stays a plain
                     view-activation button. */
                    <Select.Trigger>
                      <button
                        type="button"
                        className={styles.metricSplitCaret}
                        aria-label={`Change metric, currently ${singleMetricTitle}`}
                      >
                        <ChevronDownIcon size="xsmall" aria-hidden />
                      </button>
                    </Select.Trigger>
                  )}
                </span>
                <Select.Content className={styles.metricMenu}>
                  {singleMetricOptions.map((m) => (
                    <Select.Item key={m.key} value={m.key}>
                      <span className={styles.metricOptionLabel}>
                        <span
                          className={styles.selectItemIcon}
                          aria-hidden="true"
                        >
                          {m.icon}
                        </span>
                        {m.title}
                      </span>
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select>
            )
          }

          // Info card: bold title + description explaining what the view shows.
          // HoverCard (hover, white surface) rather than a click Popover, since
          // clicking the chip already selects the view.
          const descriptionId = `metric-info-${view.key}`

          return (
            <HoverCard key={view.key} closeDelay="fast">
              <HoverCard.Trigger>
                {/* span isolates the trigger's data-state from Chips.Item's
                  data-state so the selected chip styling is not overridden */}
                <span className={styles.tabBarTooltipWrapper}>
                  <Chips.Item value={view.key} aria-describedby={descriptionId}>
                    <span className={styles.tabIcon} aria-hidden="true">
                      {view.icon}
                    </span>
                    {view.title}
                  </Chips.Item>
                  {/* Sighted users get the title + description from the
                    HoverCard; this exposes the same copy to screen readers. */}
                  <VisuallyHidden id={descriptionId}>
                    {`${view.infoTitle ?? view.title}. ${view.description}`}
                  </VisuallyHidden>
                </span>
              </HoverCard.Trigger>
              <HoverCard.Content
                side="bottom"
                align="start"
                arrow
                className={styles.metricInfoCard}
              >
                <Text size="sm" weight="semibold">
                  {view.infoTitle ?? view.title}
                </Text>
                <Text size="sm" color="subtle">
                  {view.description}
                </Text>
              </HoverCard.Content>
            </HoverCard>
          )
        })}
      </Chips>
    </ResponsiveSegmentedControl>
  )
}
